import { useEffect, useRef } from 'react';

/**
 * Runs `callback(previous, next)` whenever `value` transitions from
 * one render to the next. Does **not** fire on mount — the first
 * render seeds the previous-value ref without invoking the callback.
 *
 * The callback receives the previous and next values so the caller
 * can match on a specific edge, e.g.:
 *
 *   useTransitionEffect(isOpen, (prev, next) => {
 *     if (!prev && next) trackOpened();
 *   });
 *
 *   useTransitionEffect(status, (prev, next) => {
 *     if (prev === 'awaiting_review' && isUnexpectedTerminalStatus(next)) {
 *       store.getState().reset();
 *     }
 *   });
 *
 * The latest callback closure is always invoked, so values captured
 * from the enclosing render are current — no stale-closure footgun.
 *
 * Equality uses `Object.is`; for object/reference values pass the
 * specific scalar field you care about instead of the whole object.
 */
export function useTransitionEffect<T>(value: T, callback: (previous: T, next: T) => void): void {
  const previousRef = useRef(value);

  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  useEffect(() => {
    const previous = previousRef.current;

    previousRef.current = value;

    if (Object.is(previous, value)) {
      return;
    }

    callbackRef.current(previous, value);
  }, [value]);
}
